/**
 * Maximum character length for every admin-configurable value — the single
 * source of truth, mirroring the backend enum. Import CONFIG_CHAR_LIMITS (or the
 * resolved `maxLength` on a ConfigField) wherever these values are created or
 * edited so the UI validation stays in lock-step with the server.
 */
export const CONFIG_CHAR_LIMITS = {
  // Inventory
  DEVICE_TYPE: 100,
  DEVICE_STATUS: 100,
  ASSET_TYPE: 100,
  CURRENCY: 10, // shared by assets and reimbursements
  ASSET_FILE_TYPE: 30,
  ASSET_FILE_SIZE: 30,

  // Employee
  DESIGNATION: 50,
  EMPLOYEE_TYPE: 30,

  // Document
  DOCUMENT_FILE_TYPE: 30,
  DOCUMENT_FILE_SIZE: 30,

  // Reimbursement
  REIMBURSEMENT_TYPE: 50,
  REIMBURSEMENT_STATUS: 30,
  REIMBURSEMENT_FILE_TYPE: 30,
  REIMBURSEMENT_FILE_SIZE: 30,

  // Leave
  LEAVE_TYPE: 60,
  LEAVE_STATUS: 30,
} as const;

export type ConfigLimitKey = keyof typeof CONFIG_CHAR_LIMITS;

/** Resolve a config value's max length by its key. */
export const getConfigCharLimit = (key: ConfigLimitKey): number => CONFIG_CHAR_LIMITS[key];

/**
 * Shared validation for an admin-config value. Returns an error message, or ""
 * when valid. Reused by every config editor so the rules never drift.
 */
export const validateConfigValue = (value: string, maxLength?: number): string => {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return "Name is required.";
  if (maxLength && trimmed.length > maxLength) {
    return `Must be ${maxLength} characters or fewer.`;
  }
  return "";
};
